import React, { FC, useMemo, useEffect } from 'react';
import { MONETATE_DEFAULT_VARIANT } from 'constants/monetate';
import { useWtrDispatch, useWtrSelector } from 'redux/hooks';
import { getAllDecisions, getAllPayloads } from 'redux/modules/experiments/selectors';
import { trackExperimentView } from 'redux/modules/experiments/actions/track-experiment-view';
import { MonetatePayload } from 'redux/modules/experiments/actions/types';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { isPageWorkersExperimentPath } from '../utils';

type OmnichannelScriptPayload = {
  type: 'OmnichannelScript';
  src: string;
  async?: boolean;
  defer?: boolean;
};

const OmnichannelScript: FC = () => {
  const { pathname } = useLocation();
  const dispatch = useWtrDispatch();
  // @ts-expect-error experiments state is not typed
  const decisions = useWtrSelector<Record<string, string>>(getAllDecisions);
  // @ts-expect-error experiments state is not typed
  const payloads = useWtrSelector<Record<string, MonetatePayload>>(getAllPayloads);

  const scripts = useMemo(() => {
    if (!payloads) return [];

    const filteredEntries = Object.entries(payloads).filter(([, payload]) => {
      return (
        typeof payload === 'object' &&
        payload !== null &&
        typeof payload.src === 'string' &&
        payload.src.trim().length > 0 &&
        typeof payload.type === 'string' &&
        payload.type === 'OmnichannelScript'
      );
    });

    return filteredEntries.map(([experimentId, payload]) => ({
      experimentId,
      payload: payload as OmnichannelScriptPayload,
    }));
  }, [payloads]);

  useEffect(() => {
    // Only allow omnichannel experimentation on offers and recipes pages
    if (!isPageWorkersExperimentPath(pathname)) {
      return;
    }

    scripts.forEach(({ experimentId }) => {
      dispatch(
        trackExperimentView({
          id: experimentId,
          variant: decisions?.[experimentId] || MONETATE_DEFAULT_VARIANT,
          trackAll: false,
        }),
      );
    });
  }, [scripts, decisions, dispatch, pathname]);

  const scriptElements = useMemo(() => {
    return scripts.map(({ experimentId, payload }) => {
      const scriptProps: {
        src: string;
        async?: boolean;
        defer?: boolean;
      } = {
        src: payload.src,
      };

      if (payload.async === true) {
        scriptProps.async = true;
      }

      if (payload.defer === true) {
        scriptProps.defer = true;
      }

      return <script key={experimentId} {...scriptProps} />;
    });
  }, [scripts]);

  // Only allow omnichannel experimentation on offers and recipes pages
  if (!isPageWorkersExperimentPath(pathname)) {
    return null;
  }

  return <Helmet>{scriptElements}</Helmet>;
};

export default OmnichannelScript;
